import React, { useState, useEffect } from 'react';
import './Settings.css';
import { settingsService } from '../../services/api';
import CloudinaryUnusedImagesModal from './CloudinaryUnusedImagesModal';
import AdminLoading from './AdminLoading';
import { getAuthHeaders, getAdminUser, setAdminUser } from '../utils/authUtils';
import { getApiBaseUrlWithoutPath } from '../../config/environment';
import { validateImageFile } from '../../utils/fileUtils';
import { adminUserService } from '../../services/adminUserService';
import {
  notifySiteBrandingUpdated,
  SITE_SETTINGS_STORAGE_KEY,
} from '../../utils/siteBranding';
import {
  normalizeContactChannelsData,
  normalizeSocialUrl,
  normalizeWhatsappDigits,
  parseContactChannelsFromStorage,
} from '../../utils/whatsappContact';

/** Backend may use phone, mobile, phoneNumber, etc., or send digits as a number. */
function stringifyPhoneField(raw) {
  if (raw == null || raw === '') return '';
  if (typeof raw === 'number' && Number.isFinite(raw)) return String(raw);
  if (typeof raw === 'string') return raw.trim();
  return '';
}

const USER_PHONE_KEYS = [
  'phone',
  'mobile',
  'mobilePhone',
  'phoneNumber',
  'contactNumber',
  'telephone',
  'whatsapp',
];

function extractPhoneFromUserLike(obj, depth = 0) {
  if (!obj || typeof obj !== 'object' || depth > 2) return '';
  for (const key of USER_PHONE_KEYS) {
    const v = stringifyPhoneField(obj[key]);
    if (v) return v;
  }
  if (obj.contact && typeof obj.contact === 'object') {
    const nested = extractPhoneFromUserLike(obj.contact, depth + 1);
    if (nested) return nested;
  }
  return '';
}

function mergeContactChannelsForForm(apiData) {
  const api = apiData && typeof apiData === 'object' ? apiData : {};
  const stored = parseContactChannelsFromStorage() || {};
  const pick = (a, b) => normalizeWhatsappDigits(a) || normalizeWhatsappDigits(b) || '';
  let chat = pick(api.whatsappChatNumber, stored.whatsappChatNumber);
  let order = pick(api.whatsappOrderNumber, stored.whatsappOrderNumber);
  if (!order && chat) order = chat;
  if (!chat && order) chat = order;
  const n = normalizeContactChannelsData({ whatsappChatNumber: chat, whatsappOrderNumber: order });
  const pickUrl = (key) =>
    normalizeSocialUrl(api[key]) || normalizeSocialUrl(stored[key]) || '';
  return {
    whatsappChatNumber: n.whatsappChatNumber || '',
    whatsappOrderNumber: n.whatsappOrderNumber || '',
    twitterUrl: pickUrl('twitterUrl'),
    facebookUrl: pickUrl('facebookUrl'),
    instagramUrl: pickUrl('instagramUrl'),
    tiktokUrl: pickUrl('tiktokUrl'),
  };
}

const Settings = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showCloudinaryUnusedModal, setShowCloudinaryUnusedModal] = useState(false);
  const isMainAdmin = getAdminUser()?.role === 'admin';
  
  // Settings states
  const [siteSettings, setSiteSettings] = useState({
    siteName: 'Super Wear',
    siteDescription: 'Premium Fashion Store',
    siteEmail: 'admin@superwear.com',
    sitePhone: '+92 300 1234567',
    siteLogoUrl: '',
    currency: 'PKR',
    timezone: 'Asia/Karachi',
    maintenanceMode: false,
    allowRegistration: true,
    requireEmailVerification: false,
    promoPopupEnabled: true,
    promoPopupImageUrl: '',
    promoPopupDelaySeconds: 3,
    promoPopupAutoCloseSeconds: 8,
    promoPopupShowOnHome: true,
    promoPopupShowOnProductPage: true,
    promoPopupShowOnShopkeeperPage: false,
  });

  const [logoUploading, setLogoUploading] = useState(false);
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    department: '',
    bio: '',
    role: '',
  });
  const [whatsappContact, setWhatsappContact] = useState({
    whatsappChatNumber: '',
    whatsappOrderNumber: '',
    twitterUrl: '',
    facebookUrl: '',
    instagramUrl: '',
    tiktokUrl: '',
  });

  const [backupSettings, setBackupSettings] = useState({
    autoBackup: true,
    backupFrequency: 'daily',
    backupRetention: 30,
    cloudBackup: false,
    lastBackup: null
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      const loadOne = async (getter, setter) => {
        try {
          const res = await getter();
          const data = (res && res.data) || res;
          if (data && typeof data === 'object') setter(data);
        } catch (e) {
          console.warn('Settings load skip:', e?.message || e);
        }
      };

      const loadSiteMerged = async () => {
        try {
          const res = await settingsService.getSite();
          const remote =
            res && res.data && typeof res.data === 'object' ? res.data : {};
          let local = {};
          try {
            const s = localStorage.getItem(SITE_SETTINGS_STORAGE_KEY);
            if (s) local = JSON.parse(s) || {};
          } catch (e) {
            /* ignore */
          }
          setSiteSettings((prev) => {
            const merged = { ...prev, ...local, ...remote };
            const hasPhone = String(merged.sitePhone || '').trim();
            if (hasPhone) return merged;
            try {
              const admin = JSON.parse(localStorage.getItem('adminUser') || '{}');
              const fromAdmin = String(admin.phone || '').trim();
              if (fromAdmin) return { ...merged, sitePhone: fromAdmin };
            } catch {
              /* ignore */
            }
            const ch = parseContactChannelsFromStorage();
            const fromWa = normalizeWhatsappDigits(
              ch?.whatsappChatNumber || ch?.whatsappOrderNumber || ''
            );
            if (fromWa) return { ...merged, sitePhone: fromWa };
            return merged;
          });
        } catch (e) {
          console.warn('Settings load skip:', e?.message || e);
          try {
            const s = localStorage.getItem(SITE_SETTINGS_STORAGE_KEY);
            if (s) {
              const local = JSON.parse(s);
              if (local && typeof local === 'object') {
                setSiteSettings((prev) => {
                  const merged = { ...prev, ...local };
                  const hasPhone = String(merged.sitePhone || '').trim();
                  if (hasPhone) return merged;
                  try {
                    const admin = JSON.parse(localStorage.getItem('adminUser') || '{}');
                    const fromAdmin = String(admin.phone || '').trim();
                    if (fromAdmin) return { ...merged, sitePhone: fromAdmin };
                  } catch {
                    /* ignore */
                  }
                  const ch = parseContactChannelsFromStorage();
                  const fromWa = normalizeWhatsappDigits(
                    ch?.whatsappChatNumber || ch?.whatsappOrderNumber || ''
                  );
                  if (fromWa) return { ...merged, sitePhone: fromWa };
                  return merged;
                });
              }
            }
          } catch (e2) {
            /* ignore */
          }
        }
      };

      const loadAdminProfile = async () => {
        const localAdmin = getAdminUser() || JSON.parse(localStorage.getItem('adminUser') || '{}');
        try {
          const adminToken = localStorage.getItem('adminToken');
          if (!adminToken) {
            setProfileData({
              name: localAdmin.name || '',
              email: localAdmin.email || '',
              phone: extractPhoneFromUserLike(localAdmin) || '',
              department: localAdmin.department || '',
              bio: localAdmin.bio || '',
              role: localAdmin.role || '',
            });
            return;
          }
          const response = await fetch(`${getApiBaseUrlWithoutPath()}/api/admin/profile`, {
            headers: {
              Authorization: `Bearer ${adminToken}`,
              'Content-Type': 'application/json',
            },
          });
          if (!response.ok) throw new Error(`Profile load failed (${response.status})`);
          const data = await response.json().catch(() => ({}));
          const p =
            (data.profile && typeof data.profile === 'object' && data.profile) ||
            (data.data?.profile && typeof data.data.profile === 'object' && data.data.profile) ||
            (data.user && typeof data.user === 'object' && data.user) ||
            (data.data?.user && typeof data.data.user === 'object' && data.data.user) ||
            {};
          setProfileData({
            name: p.name || localAdmin.name || '',
            email: p.email || localAdmin.email || '',
            phone: extractPhoneFromUserLike(p) || extractPhoneFromUserLike(localAdmin) || '',
            department: p.department || localAdmin.department || '',
            bio: p.bio || localAdmin.bio || '',
            role: p.role || localAdmin.role || '',
          });
        } catch (e) {
          console.warn('Profile load skip:', e?.message || e);
          setProfileData({
            name: localAdmin.name || '',
            email: localAdmin.email || '',
            phone: extractPhoneFromUserLike(localAdmin) || '',
            department: localAdmin.department || '',
            bio: localAdmin.bio || '',
            role: localAdmin.role || '',
          });
        }
      };

      const loadContactChannels = async () => {
        try {
          const chRes = await settingsService.getContactChannelsAdmin();
          const ch = chRes?.data || chRes;
          setWhatsappContact(mergeContactChannelsForForm(ch && typeof ch === 'object' ? ch : {}));
        } catch (e) {
          console.warn('Contact channels load skip:', e?.message || e);
          setWhatsappContact(mergeContactChannelsForForm({}));
        }
      };

      await Promise.all([
        loadSiteMerged(),
        loadAdminProfile(),
        loadContactChannels(),
        loadOne(settingsService.getBackup, setBackupSettings),
      ]);
    } catch (error) {
      console.error('Error loading settings:', error);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSiteSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await settingsService.saveSite(siteSettings);
      if (res && res.success === false) throw new Error(res.error || res.message || 'Save failed');
      setSuccess('Site settings saved successfully!');
      setTimeout(() => setSuccess(null), 3000);
      notifySiteBrandingUpdated();
      window.dispatchEvent(new CustomEvent('maintenanceModeChanged'));
    } catch (error) {
      console.error('Error saving site settings:', error);
      setError(error?.message || 'Failed to save site settings');
    } finally {
      setLoading(false);
    }
  };

  const saveProfileInformation = async () => {
    try {
      setLoading(true);
      setError(null);
      const phoneTrimmed = String(profileData.phone || '').trim();
      const profilePayload = {
        name: profileData.name,
        email: profileData.email,
        phone: phoneTrimmed,
        bio: profileData.bio,
        department: profileData.department,
      };

      const adminToken = localStorage.getItem('adminToken');
      if (adminToken) {
        await fetch(`${getApiBaseUrlWithoutPath()}/api/admin/profile`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${adminToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(profilePayload),
        }).catch(() => null);
      }

      const userData = getAdminUser() || JSON.parse(localStorage.getItem('adminUser') || '{}');
      const updatedUser = { ...userData, ...profilePayload };
      setAdminUser(updatedUser);

      const userId = updatedUser?._id || updatedUser?.id;
      if (userId && phoneTrimmed) {
        try {
          await adminUserService.updateUser(userId, { phone: phoneTrimmed });
        } catch (e) {
          console.warn('Optional user phone sync failed:', e?.message || e);
        }
      }

      setProfileData((prev) => ({ ...prev, phone: phoneTrimmed }));
      setSuccess('Profile information saved successfully!');
      setTimeout(() => setSuccess(null), 3000);
      window.dispatchEvent(new CustomEvent('adminProfileUpdated', { detail: updatedUser }));
    } catch (error) {
      console.error('Error saving profile information:', error);
      setError(error?.message || 'Failed to save profile information');
    } finally {
      setLoading(false);
    }
  };

  const saveContactChannels = async () => {
    try {
      setLoading(true);
      setError(null);
      const fresh = await settingsService.getContactChannelsAdmin();
      const base = normalizeContactChannelsData(fresh?.data || fresh || {});
      const payload = normalizeContactChannelsData({
        ...base,
        whatsappChatNumber: whatsappContact.whatsappChatNumber.trim(),
        whatsappOrderNumber: whatsappContact.whatsappOrderNumber.trim(),
        twitterUrl: whatsappContact.twitterUrl,
        facebookUrl: whatsappContact.facebookUrl,
        instagramUrl: whatsappContact.instagramUrl,
        tiktokUrl: whatsappContact.tiktokUrl,
      });
      const res = await settingsService.saveContactChannels(payload);
      if (res && res.success === false) throw new Error(res.error || res.message || 'Save failed');
      setSuccess('WhatsApp numbers and social links saved successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error saving contact channels:', error);
      setError(error?.message || 'Failed to save contact channels');
    } finally {
      setLoading(false);
    }
  };

  const saveBackupSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await settingsService.saveBackup(backupSettings);
      if (res && res.success === false) throw new Error(res.error || res.message || 'Save failed');
      setSuccess('Backup settings saved successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error saving backup settings:', error);
      setError(error?.message || 'Failed to save backup settings');
    } finally {
      setLoading(false);
    }
  };

  const createBackup = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await settingsService.createBackup();
      if (res && res.success === false) throw new Error(res.error || res.message || 'Backup failed');
      setSuccess('Backup created successfully!');
      setTimeout(() => setSuccess(null), 3000);
      loadSettings();
    } catch (error) {
      console.error('Error creating backup:', error);
      setError(error?.message || 'Failed to create backup');
    } finally {
      setLoading(false);
    }
  };

  const handleSiteLogoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const v = validateImageFile(file, 10);
    if (!v.isValid) {
      setError(v.errors.join(' '));
      return;
    }
    try {
      setLogoUploading(true);
      setError(null);
      const headers = getAuthHeaders();
      if (!headers.Authorization) {
        setError('Logo upload requires an admin session.');
        return;
      }
      const apiUrl = getApiBaseUrlWithoutPath();
      const formData = new FormData();
      formData.append('images', file);
      const response = await fetch(`${apiUrl}/api/admin/upload`, {
        method: 'POST',
        headers: { Authorization: headers.Authorization },
        body: formData,
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.message || `Upload failed (${response.status})`);
      }
      const uploads = result.data?.uploads;
      if (!result.success || !uploads?.length || !uploads[0].url) {
        throw new Error(result.message || 'No image URL returned');
      }
      setSiteSettings((prev) => ({ ...prev, siteLogoUrl: uploads[0].url }));
      setSuccess('Logo uploaded. Click Save to apply site-wide.');
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      console.error('Logo upload error:', err);
      setError(err?.message || 'Logo upload failed');
    } finally {
      setLogoUploading(false);
    }
  };

  const clearSiteLogo = () => {
    setSiteSettings((prev) => ({ ...prev, siteLogoUrl: '' }));
  };

  const clearCache = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await settingsService.clearCache();
      if (res && res.success === false) throw new Error(res.error || res.message || 'Clear cache failed');
      setSuccess('Cache cleared successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error clearing cache:', error);
      setError(error?.message || 'Failed to clear cache');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !siteSettings.siteName) {
    return (
      <AdminLoading
        title="Loading Settings"
        subtitle="Fetching configuration..."
        icon="⚙️"
      />
    );
  }

  return (
    <div className="settings-container">
      <div className="settings-header">
        <div className="header-content">
          <h1>Settings</h1>
          <p>Manage your store configuration and preferences</p>
        </div>
        <div className="header-actions">
          <button onClick={loadSettings} className="refresh-btn" disabled={loading}>
            <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`}></i>
            Refresh
          </button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="success-message">
          <i className="fas fa-check-circle"></i>
          {success}
        </div>
      )}
      {error && (
        <div className="error-message">
          <i className="fas fa-exclamation-circle"></i>
          {error}
        </div>
      )}

      <div className="settings-grid">
        {/* Profile Information */}
        <div className="settings-card">
          <div className="card-header">
            <h3><i className="fas fa-user"></i> Profile Information</h3>
            <button onClick={saveProfileInformation} className="save-btn" disabled={loading}>
              <i className="fas fa-save"></i> Save
            </button>
          </div>
          <div className="card-content">
            <div className="form-group">
              <label>Full Name</label>
              <input
                type="text"
                value={profileData.name ?? ''}
                onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={profileData.email ?? ''}
                onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Phone</label>
              <input
                type="tel"
                value={profileData.phone ?? ''}
                onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                className="form-input"
                placeholder="+92 300 1234567"
              />
            </div>
            <div className="form-group">
              <label>Department</label>
              <input
                type="text"
                value={profileData.department ?? ''}
                onChange={(e) => setProfileData({ ...profileData, department: e.target.value })}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Bio</label>
              <textarea
                value={profileData.bio ?? ''}
                onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                className="form-input"
                rows={3}
              />
            </div>
          </div>
        </div>

        {/* WhatsApp (customer contact) */}
        <div className="settings-card">
          <div className="card-header">
            <h3><i className="fab fa-whatsapp"></i> WhatsApp (customer contact)</h3>
            <button onClick={saveContactChannels} className="save-btn" disabled={loading}>
              <i className="fas fa-save"></i> Save
            </button>
          </div>
          <div className="card-content">
            <p className="section-description">
              Checkout mein chat/order WhatsApp buttons ke liye yahi numbers use hote hain. Neeche X, Facebook, Instagram,
              TikTok links footer (WhatsApp ke sath), Follow Us, aur Contact Us par dikhenge.
            </p>
            <div className="form-group">
              <label>WhatsApp - general chat / help</label>
              <input
                type="tel"
                value={whatsappContact.whatsappChatNumber ?? ''}
                onChange={(e) =>
                  setWhatsappContact({ ...whatsappContact, whatsappChatNumber: e.target.value })
                }
                className="form-input"
                placeholder="923001234567"
              />
            </div>
            <div className="form-group">
              <label>WhatsApp - order line</label>
              <input
                type="tel"
                value={whatsappContact.whatsappOrderNumber ?? ''}
                onChange={(e) =>
                  setWhatsappContact({ ...whatsappContact, whatsappOrderNumber: e.target.value })
                }
                className="form-input"
                placeholder="923009876543"
              />
            </div>
            <div className="form-group">
              <label>Twitter / X profile URL</label>
              <input
                type="url"
                value={whatsappContact.twitterUrl ?? ''}
                onChange={(e) =>
                  setWhatsappContact({ ...whatsappContact, twitterUrl: e.target.value })
                }
                className="form-input"
                placeholder="https://twitter.com/yourstore"
              />
            </div>
            <div className="form-group">
              <label>Facebook page URL</label>
              <input
                type="url"
                value={whatsappContact.facebookUrl ?? ''}
                onChange={(e) =>
                  setWhatsappContact({ ...whatsappContact, facebookUrl: e.target.value })
                }
                className="form-input"
                placeholder="https://facebook.com/yourstore"
              />
            </div>
            <div className="form-group">
              <label>Instagram profile URL</label>
              <input
                type="url"
                value={whatsappContact.instagramUrl ?? ''}
                onChange={(e) =>
                  setWhatsappContact({ ...whatsappContact, instagramUrl: e.target.value })
                }
                className="form-input"
                placeholder="https://instagram.com/yourstore"
              />
            </div>
            <div className="form-group">
              <label>TikTok profile URL</label>
              <input
                type="url"
                value={whatsappContact.tiktokUrl ?? ''}
                onChange={(e) =>
                  setWhatsappContact({ ...whatsappContact, tiktokUrl: e.target.value })
                }
                className="form-input"
                placeholder="https://www.tiktok.com/@yourstore"
              />
            </div>
          </div>
        </div>

        {/* Site Settings */}
        <div className="settings-card">
          <div className="card-header">
            <h3><i className="fas fa-globe"></i> Site Settings</h3>
            <button onClick={saveSiteSettings} className="save-btn" disabled={loading}>
              <i className="fas fa-save"></i> Save
            </button>
          </div>
          <div className="card-content">
            <div className="form-group">
              <label>Site Name</label>
              <input
                type="text"
                value={siteSettings.siteName ?? ''}
                onChange={(e) => setSiteSettings({...siteSettings, siteName: e.target.value})}
                className="form-input"
              />
            </div>
            <div className="form-group logo-upload-group">
              <label>Store logo</label>
              <p className="form-hint logo-upload-hint">
                Used on the shop header, browser tab icon (favicon), printed invoices, and order confirmation. Square or wide PNG/WebP recommended (max 10MB).
              </p>
              <div className="logo-upload-row">
                {siteSettings.siteLogoUrl ? (
                  <div className="logo-preview-wrap">
                    <img src={siteSettings.siteLogoUrl} alt="Logo preview" className="logo-preview-img" />
                    <button
                      type="button"
                      className="btn-remove-logo"
                      onClick={clearSiteLogo}
                      disabled={loading || logoUploading}
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="logo-preview-placeholder">No logo</div>
                )}
                <label className={`logo-file-label ${logoUploading || loading ? 'disabled' : ''}`}>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleSiteLogoFile}
                    disabled={loading || logoUploading}
                    className="logo-file-input"
                  />
                  <i className="fas fa-cloud-upload-alt" />
                  {logoUploading ? 'Uploading…' : 'Upload logo'}
                </label>
              </div>
            </div>
            <div className="form-group">
              <label>Site Description</label>
              <textarea
                value={siteSettings.siteDescription ?? ''}
                onChange={(e) => setSiteSettings({...siteSettings, siteDescription: e.target.value})}
                className="form-textarea"
                rows="3"
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="site-settings-email">Email</label>
                <input
                  id="site-settings-email"
                  type="email"
                  value={siteSettings.siteEmail ?? ''}
                  onChange={(e) => setSiteSettings({...siteSettings, siteEmail: e.target.value})}
                  className="form-input"
                  autoComplete="email"
                />
              </div>
              <div className="form-group">
                <label htmlFor="site-settings-phone">Phone (site contact)</label>
                <input
                  id="site-settings-phone"
                  type="tel"
                  value={siteSettings.sitePhone ?? ''}
                  onChange={(e) => setSiteSettings({...siteSettings, sitePhone: e.target.value})}
                  className="form-input"
                  placeholder="+92 300 1234567"
                  autoComplete="tel"
                />
                <small className="form-hint">
                  Public contact number (footer, contact page, etc.). Saved numbers from My Account or WhatsApp load here
                  if empty — edit and click <strong>Save</strong> to store.
                </small>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Currency</label>
                <select
                  value={siteSettings.currency ?? 'PKR'}
                  onChange={(e) => setSiteSettings({...siteSettings, currency: e.target.value})}
                  className="form-select"
                >
                  <option value="PKR">PKR (Pakistani Rupee)</option>
                  <option value="USD">USD (US Dollar)</option>
                  <option value="EUR">EUR (Euro)</option>
                </select>
              </div>
              <div className="form-group">
                <label>Timezone</label>
                <select
                  value={siteSettings.timezone ?? 'Asia/Karachi'}
                  onChange={(e) => setSiteSettings({...siteSettings, timezone: e.target.value})}
                  className="form-select"
                >
                  <option value="Asia/Karachi">Asia/Karachi</option>
                  <option value="UTC">UTC</option>
                  <option value="America/New_York">America/New_York</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={!!siteSettings.maintenanceMode}
                  onChange={(e) => setSiteSettings({...siteSettings, maintenanceMode: e.target.checked})}
                />
                <span className="checkmark"></span>
                Maintenance Mode
              </label>
              <small className="form-hint">When enabled, visitors see a maintenance page. Admin panel stays accessible.</small>
            </div>
          </div>
        </div>

        {/* Backup Settings */}
        <div className="settings-card">
          <div className="card-header">
            <h3><i className="fas fa-database"></i> Backup Settings</h3>
            <button onClick={saveBackupSettings} className="save-btn" disabled={loading}>
              <i className="fas fa-save"></i> Save
            </button>
          </div>
          <div className="card-content">
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={!!backupSettings.autoBackup}
                  onChange={(e) => setBackupSettings({...backupSettings, autoBackup: e.target.checked})}
                />
                <span className="checkmark"></span>
                Auto Backup
              </label>
            </div>
            <div className="form-group">
              <label>Backup Frequency</label>
              <select
                value={backupSettings.backupFrequency ?? 'daily'}
                onChange={(e) => setBackupSettings({...backupSettings, backupFrequency: e.target.value})}
                className="form-select"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <div className="form-group">
              <label>Backup Retention (days)</label>
              <input
                type="number"
                value={backupSettings.backupRetention ?? 30}
                onChange={(e) => setBackupSettings({...backupSettings, backupRetention: parseInt(e.target.value, 10)})}
                className="form-input"
                min="7"
                max="365"
              />
            </div>
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={!!backupSettings.cloudBackup}
                  onChange={(e) => setBackupSettings({...backupSettings, cloudBackup: e.target.checked})}
                />
                <span className="checkmark"></span>
                Cloud Backup
              </label>
            </div>
            {backupSettings.lastBackup && (
              <div className="backup-info">
                <p><strong>Last Backup:</strong> {backupSettings.lastBackup ? new Date(backupSettings.lastBackup).toLocaleString() : 'Never'}</p>
              </div>
            )}
            <div className="backup-actions">
              <button onClick={createBackup} className="backup-btn" disabled={loading}>
                <i className="fas fa-download"></i> Create Backup Now
              </button>
            </div>
          </div>
        </div>

        {/* System Actions */}
        <div className="settings-card">
          <div className="card-header">
            <h3><i className="fas fa-cogs"></i> System Actions</h3>
          </div>
          <div className="card-content">
            <div className="system-actions">
              <button onClick={clearCache} className="system-btn" disabled={loading}>
                <i className="fas fa-trash"></i> Clear Cache
              </button>
              <button onClick={loadSettings} className="system-btn" disabled={loading}>
                <i className="fas fa-sync-alt"></i> Refresh Settings
              </button>
              {isMainAdmin && (
                <button
                  type="button"
                  onClick={() => setShowCloudinaryUnusedModal(true)}
                  className="system-btn system-btn--cloudinary"
                  disabled={loading}
                >
                  <i className="fas fa-cloud"></i> Cloudinary Image Check
                </button>
              )}
            </div>
            {isMainAdmin && (
              <p className="system-actions-hint">
                Cloudinary Image Check finds uploaded images that are not linked to any product, category, or site setting.
              </p>
            )}
          </div>
        </div>
      </div>

      {isMainAdmin && (
        <CloudinaryUnusedImagesModal
          isOpen={showCloudinaryUnusedModal}
          onClose={() => setShowCloudinaryUnusedModal(false)}
          onDeleted={(count) => {
            setSuccess(`${count} unused Cloudinary image(s) deleted.`);
            setTimeout(() => setSuccess(null), 4000);
          }}
        />
      )}
    </div>
  );
};

export default Settings;